import { AfterViewInit, ChangeDetectorRef, Component, DestroyRef, inject, OnInit, ChangeDetectionStrategy, viewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatStep, MatStepLabel, MatStepper } from '@angular/material/stepper';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { FormsModule } from '@angular/forms';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatOption, MatSelect, MatSelectTrigger } from '@angular/material/select';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { MatButton, MatIconButton } from '@angular/material/button';
import {
	BillingPeriod,
	CostAllocation,
	CostGroupCategory,
	CostGroupService,
	CustomCostAllocation,
	CustomCostAllocationService,
	OrdinaryAllocationType,
	OrdinaryCostAllocation,
	OrdinaryCostAllocationService,
	ShareCostAllocation,
	ShareCostAllocationService,
	TenantSelection,
	TenantSelectionService,
	TenantShare,
	TenantShareService,
	TenantShareTypeMeta,
} from '@eadsysteme/billingclient-v1';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { SessionService } from '../../../../../../services/session.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, of, zip } from 'rxjs';
import { MatInput } from '@angular/material/input';
import { TranslateEnumPipe } from '../../../../../../util/TranslateEnumPipe';
import { SnackbarService } from '../../../../../../services/snackbar.service';
import { MatTooltip } from '@angular/material/tooltip';
import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { CostAllocationDataType } from '../../../../../../util/models/costAllocation/cost-allocation-type';

export interface StepperResult {
	costGroupCategory: CostGroupCategory;
	shareType: CostAllocationDataType;
	tenantShare?: TenantShare;
	ordinaryShare?: OrdinaryAllocationType;
	tenantSelection?: TenantSelection;
	onlyFlatrate: boolean;
	name?: string;
}

@Component({
	selector: 'vex-allocation-dialog',
	imports: [
		MatDialogActions,
		MatDialogContent,
		MatStepper,
		MatStep,
		MatRadioGroup,
		MatRadioButton,
		FormsModule,
		MatStepLabel,
		MatFormField,
		MatLabel,
		MatSelect,
		MatOption,
		MatCard,
		MatCardContent,
		MatIcon,
		MatButton,
		TranslocoDirective,
		MatDialogTitle,
		MatSelectTrigger,
		MatInput,
		TranslateEnumPipe,
		MatTooltip,
		CdkDrag,
		CdkDragHandle,
		MatIconButton,
	],
	templateUrl: './allocation-dialog.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './allocation-dialog.component.scss',
})
export class AllocationDialogComponent implements OnInit, AfterViewInit {
	dialogRef = inject<MatDialogRef<AllocationDialogComponent>>(MatDialogRef);
	data = inject(MAT_DIALOG_DATA);
	private readonly sessionService = inject(SessionService);
	private readonly customCostAllocationService = inject(CustomCostAllocationService);
	private readonly ordinaryCostAllocationService = inject(OrdinaryCostAllocationService);
	private readonly shareCostAllocationService = inject(ShareCostAllocationService);
	private readonly tenantShareService = inject(TenantShareService);
	private readonly tenantSelectionService = inject(TenantSelectionService);
	private readonly translocoService = inject(TranslocoService);
	private readonly costGroupService = inject(CostGroupService);
	private readonly cd = inject(ChangeDetectorRef);

	private readonly destroyRef = inject(DestroyRef);

	currentStep = 0;
	formData: StepperResult = {
		costGroupCategory: undefined,
		shareType: undefined,
		tenantShare: undefined,
		tenantSelection: undefined,
		onlyFlatrate: false,
		name: undefined,
	};
	protected readonly ordinaryAllocationTypes: OrdinaryAllocationType[] = Object.values(OrdinaryAllocationType);
	protected readonly tenantShareTypeMeta = TenantShareTypeMeta;
	protected readonly costAllocationIdToEdit: string;

	private readonly billingId: string;
	private readonly billingPeriod: BillingPeriod;
	private readonly forcedValidSteps = new Set<number>();

	private readonly stepper = viewChild.required<MatStepper>('stepper');

	tenantShares: TenantShare[] = [];
	tenantSelections: TenantSelection[] = [];
	protected translatedOrdinaryAllocationTypes: Map<OrdinaryAllocationType, string> = new Map();
	private snackBar = inject(SnackbarService);

	constructor() {
		const data = this.data;
		const tenantShareService = this.tenantShareService;
		const tenantSelectionService = this.tenantSelectionService;

		this.costAllocationIdToEdit = data.costAllocationIdToEdit;
		this.billingId = data.billingId;
		this.billingPeriod = data.billingPeriod;

		this.ordinaryAllocationTypes
			.map((value) => ({
				value,
				label: this.translocoService.translate('billing.ordinaryAllocationType.' + value),
			}))
			.sort((a, b) => a.label.localeCompare(b.label))
			.forEach((value) => this.translatedOrdinaryAllocationTypes.set(value.value, value.label));

		zip(
			tenantShareService.readAllTenantShare(this.sessionService.effectiveArea, this.billingId),
			tenantSelectionService.readAllTenantSelection(this.sessionService.effectiveArea, this.billingId)
		)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: ([shares, selections]) => {
					this.tenantShares = shares;
					this.tenantSelections = selections;

					this.tenantShares.sort((a, b) => a.name.localeCompare(b.name));
					this.tenantSelections.sort((a, b) => a.name.localeCompare(b.name));
				},
			});
	}

	ngOnInit(): void {
		// Edit-Modus: ID ohne dtype → typabhängiges Read der Reihe nach (Custom → Ordinary → Share).
		if (this.costAllocationIdToEdit) {
			const area = this.sessionService.effectiveArea;
			const billing = this.billingId;
			const allocationId = this.costAllocationIdToEdit;
			this.customCostAllocationService
				.readCustomCostAllocation(area, billing, allocationId)
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe({
					next: (allocation) => this.reloadAllocationData(allocation),
					error: () => {
						this.ordinaryCostAllocationService
							.readOrdinaryCostAllocation(area, billing, allocationId)
							.pipe(takeUntilDestroyed(this.destroyRef))
							.subscribe({
								next: (allocation) => this.reloadAllocationData(allocation),
								error: () => {
									this.shareCostAllocationService
										.readShareCostAllocation(area, billing, allocationId)
										.pipe(takeUntilDestroyed(this.destroyRef))
										.subscribe({
											next: (allocation) => this.reloadAllocationData(allocation),
										});
								},
							});
					},
				});
		}
	}

	ngAfterViewInit() {
		if (this.costAllocationIdToEdit) {
			this.goToStep(4);
			this.cd.detectChanges();
		}
	}

	private reloadAllocationData(costAllocation: CostAllocation | CustomCostAllocation | OrdinaryCostAllocation | ShareCostAllocation) {
		this.goToStep(4);
		const tenantShareExportId = (costAllocation as ShareCostAllocation).tenantShareExportId;
		const tenantSelectionExportId = costAllocation.tenantSelectionExportId;
		zip(
			tenantShareExportId ? this.tenantShareService.readTenantShare(this.sessionService.effectiveArea, this.billingId, tenantShareExportId) : of(null),
			tenantSelectionExportId
				? this.tenantSelectionService.readTenantSelection(this.sessionService.effectiveArea, this.billingId, tenantSelectionExportId)
				: of(null)
		)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: ([share, selection]) => {
					this.formData.costGroupCategory = costAllocation.costGroupCategory;
					this.formData.shareType = costAllocation.dtype as CostAllocationDataType;
					this.formData.name = costAllocation.name;
					this.formData.ordinaryShare = (costAllocation as OrdinaryCostAllocation).type;
					this.formData.tenantShare = share;
					this.formData.tenantSelection = selection;
				},
			});
	}

	get step1Valid(): boolean {
		return this.forcedValidSteps.has(0) || this.formData.costGroupCategory != null;
	}

	get step2Valid(): boolean {
		return this.forcedValidSteps.has(1) || this.formData.tenantShare != null || this.formData.ordinaryShare != null;
	}

	get step3Valid(): boolean {
		if (this.forcedValidSteps.has(2)) return true;
		return this.step2Valid;
	}

	goToStep(index: number) {
		const wasLinear = this.stepper().linear;
		this.stepper().linear = false;

		for (let i = 0; i < index; i++) {
			this.forcedValidSteps.add(i);
		}
		this.currentStep = index;
		this.stepper().selectedIndex = index;

		setTimeout(() => {
			this.stepper().linear = wasLinear;
			this.forcedValidSteps.clear();
		});
	}

	isConfigurationComplete(): boolean {
		return this.step1Valid && this.step2Valid && this.step3Valid && !!this.formData.name;
	}

	compareObjects(a: any, b: any): boolean {
		return a && b ? a.exportId === b.exportId : a === b;
	}

	compareOrdinaryAllocations(a: any, b: any): boolean {
		return a?.toString() === b?.toString();
	}

	canProceed(): boolean {
		switch (this.currentStep) {
			case 0:
				return this.step1Valid;
			case 1:
				return this.step2Valid;
			case 2:
				return this.step3Valid;
			case 3:
				return this.step3Valid;
			default:
				return false;
		}
	}

	nextStep(): void {
		if (this.canProceed() && this.currentStep < 4) {
			this.currentStep++;
		}
	}

	previousStep(): void {
		if (this.currentStep > 0) {
			this.currentStep--;
		}
	}

	onCancel(): void {
		this.dialogRef.close(null);
	}

	isTenantShare(value: unknown): value is TenantShare {
		return typeof value === 'object' && value !== null && 'exportId' in value;
	}

	deleteShareAndFollowing() {
		this.formData.tenantShare = undefined;
		this.formData.tenantSelection = undefined;
		this.formData.ordinaryShare = undefined;
	}

	onComplete(): void {
		if (!this.isConfigurationComplete()) {
			return;
		}
		const area = this.sessionService.effectiveArea;
		const periodExportId = this.billingPeriod.exportId;

		// Routing nach dtype über die typspezialisierten Services (Share/Ordinary/Custom).
		let request$: Observable<CostAllocation | CustomCostAllocation | OrdinaryCostAllocation | ShareCostAllocation>;
		const isUpdate = !!this.costAllocationIdToEdit;
		switch (this.formData.shareType) {
			case CostAllocationDataType.SHARE: {
				const body: ShareCostAllocation = {
					exportId: this.costAllocationIdToEdit,
					costGroupCategory: this.formData.costGroupCategory,
					dtype: this.formData.shareType,
					name: this.formData.name,
					tenantSelectionExportId: this.formData.tenantSelection?.exportId,
					tenantShareExportId: this.isTenantShare(this.formData.tenantShare) ? this.formData.tenantShare.exportId : undefined,
				};
				request$ = isUpdate
					? this.shareCostAllocationService.updateShareCostAllocation(area, this.billingId, this.costAllocationIdToEdit, body)
					: this.shareCostAllocationService.createShareCostAllocation(area, this.billingId, periodExportId, body);
				break;
			}
			case CostAllocationDataType.ORDINARY: {
				const body: OrdinaryCostAllocation = {
					exportId: this.costAllocationIdToEdit,
					costGroupCategory: this.formData.costGroupCategory,
					dtype: this.formData.shareType,
					name: this.formData.name,
					tenantSelectionExportId: this.formData.tenantSelection?.exportId,
					type: this.formData.ordinaryShare,
				};
				request$ = isUpdate
					? this.ordinaryCostAllocationService.updateOrdinaryCostAllocation(area, this.billingId, this.costAllocationIdToEdit, body)
					: this.ordinaryCostAllocationService.createOrdinaryCostAllocation(area, this.billingId, periodExportId, body);
				break;
			}
			case CostAllocationDataType.CUSTOM:
			default: {
				const body: CustomCostAllocation = {
					exportId: this.costAllocationIdToEdit,
					costGroupCategory: this.formData.costGroupCategory,
					dtype: this.formData.shareType,
					name: this.formData.name,
					tenantSelectionExportId: this.formData.tenantSelection?.exportId,
				};
				request$ = isUpdate
					? this.customCostAllocationService.updateCustomCostAllocation(area, this.billingId, this.costAllocationIdToEdit, body)
					: this.customCostAllocationService.createCustomCostAllocation(area, this.billingId, periodExportId, body);
				break;
			}
		}

		request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
			next: () => {
				this.snackBar.success(this.translocoService.translate('billing.savedAllocation'));
				this.dialogRef.close(true);
			},
			error: (err) => {
				this.snackBar.error(this.translocoService.translate('billing.savedAllocation') + ': ' + err.message);
			},
		});
	}

	getTenantShareName() {
		return this.formData.tenantShare?.name ?? '';
	}

	getTenantSelectionName() {
		return this.formData.tenantSelection?.name ?? '';
	}

	printAddInfo(tenantShare: TenantShare) {
		const abbreviation = tenantShare.abbreviation ? this.translocoService.translate('common.abbreviation') + ': ' + tenantShare.abbreviation : '';
		const printName = tenantShare.printname ? this.translocoService.translate('common.printName') + ': ' + tenantShare.printname : '';
		const strings = [abbreviation, printName];
		const filtered = strings.filter((str) => str.trim() !== '');

		if (filtered.length <= 1) {
			return filtered[0] ? '(' + filtered[0] + ')' : ''; // Einzelnes Element oder leerer String
		}

		return '(' + filtered.join(',') + ')';
	}

	protected readonly CostAllocationDataType = CostAllocationDataType;
}
